import type { User } from '@supabase/supabase-js'
import type { PermissionMap } from '#shared/types/api'
import type { AuthErrorResponse, AuthResult, LoginResponse, RegisterResponse, UserData, UserMetadata } from '#shared/types/auth'
import { defineStore } from 'pinia'

/**
 * Authentication Store
 * Centralized state management for user authentication and session
 *
 * BENEFITS OVER COMPOSABLES:
 * - Single source of truth for user state across all components
 * - Persists user data between page navigations
 * - Automatic reactivity for all components watching auth state
 * - Cleaner API for permission checks
 *
 * USAGE:
 * ```typescript
 * const authStore = useAuthStore()
 *
 * // Check authentication
 * if (authStore.isAuthenticated) { ... }
 *
 * // Check permissions
 * if (authStore.can('camps', 'create')) { ... }
 *
 * // Get user data
 * const email = authStore.user?.email
 * const fullName = authStore.userData?.full_name
 * ```
 */

interface AuthState {

  // Core user session (from Supabase)
  user: User | null

  // Extended user data (from /api/me)
  userData: UserData | null

  // Loading states
  loading: boolean
  syncing: boolean

  // Error state
  error: string | null

  // Initialization flag
  initialized: boolean
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    user: null,
    userData: null,
    loading: false,
    syncing: false,
    error: null,
    initialized: false,
  }),

  getters: {
    /**
     * Check if user is authenticated
     */
    isAuthenticated(state): boolean {
      return !!state.user
    },

    /**
     * Get user ID
     */
    userId(state): string | null {
      return state.user?.id ?? null
    },

    /**
     * Get user email
     */
    userEmail(state): string | null {
      return state.user?.email ?? null
    },

    /**
     * Get user's full name from userData
     */
    fullName(state): string | null {
      return state.userData?.fullName ?? null
    },

    /**
     * Get user's display name (full name or email)
     */
    displayName(): string {
      return this.fullName || this.userEmail || 'Guest'
    },

    /**
     * Get permissions map (resource -> action -> boolean)
     */
    permissions(state): PermissionMap {
      // API returns PermissionMap structure: { resource: { action: boolean } }
      return (state.userData?.permissions as unknown as PermissionMap) ?? {}
    },

    /**
     * Check if user data is loaded
     */
    hasUserData(state): boolean {
      return !!state.userData
    },

    /**
     * Check if any loading is in progress
     */
    isLoading(state): boolean {
      return state.loading || state.syncing
    },
  },

  actions: {
    /**
     * Initialize the auth store
     * Should be called once on app mount
     */
    async initialize() {
      if (this.initialized)
        return

      try {
        this.loading = true

        // Get the current Supabase user from the module
        const supabaseUser = useSupabaseUser()

        if (supabaseUser.value) {
          // Store user directly from Supabase module
          this.user = supabaseUser.value ? { ...supabaseUser.value } as unknown as User : null
          await this.syncUserData()
        }

        this.initialized = true
      }
      catch (error) {
        console.error('[AuthStore] Initialization error:', error)
        this.error = 'Failed to initialize authentication'
      }
      finally {
        this.loading = false
      }
    },

    /**
     * Sync user data and permissions from server
     * Fetches extended profile data and permission rules
     */
    async syncUserData(retryCount: number = 0, maxRetries: number = 3): Promise<void> {
      // Don't sync if no user
      if (!this.user) {
        this.userData = null

        const sharedUserData = useState<UserData | null>('auth-user-data', () => null)

        sharedUserData.value = null

        return
      }

      // Prevent concurrent syncs
      if (this.syncing)
        return

      try {
        this.syncing = true
        this.error = null

        const supabase = useSupabaseClient()

        // Get current session
        const { data: { session } } = await supabase.auth.getSession()

        if (!session?.access_token) {
          throw new Error('No access token available')
        }

        // Fetch user data from server
        const userData = await $fetch('/api/me', {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        })

        // Validate response
        if (userData && !('error' in userData)) {
          this.userData = userData as unknown as UserData

          const sharedUserData = useState<UserData | null>('auth-user-data', () => null)

          sharedUserData.value = this.userData
        }
      }
      catch (error) {
        const authError = error as AuthErrorResponse

        // Handle rate limiting
        if (authError.statusCode === 429) {
          console.error('[AuthStore] Rate limit exceeded')
          this.error = 'Too many requests. Please try again later.'

          return
        }

        // Retry on 401 (race condition after sign-in)
        if (
          authError.statusCode === 401
          && retryCount < maxRetries
          && this.user
        ) {
          // Exponential backoff: 1s, 2s, 4s
          await new Promise(resolve => setTimeout(resolve, 1000 * (2 ** retryCount)))

          return this.syncUserData(retryCount + 1, maxRetries)
        }

        // Log non-401 errors
        if (authError.statusCode !== 401) {
          console.error('[AuthStore] Sync error:', error)
          this.error = 'Failed to load user data'
        }

        this.userData = null

        const sharedUserData = useState<UserData | null>('auth-user-data', () => null)

        sharedUserData.value = null
      }
      finally {
        this.syncing = false
      }
    },

    /**
     * Login with email and password
     */
    async login(email: string, password: string): Promise<AuthResult<LoginResponse>> {
      try {
        this.loading = true
        this.error = null

        const { signInWithEmail } = useSupabaseAuth()
        const result = await signInWithEmail(email, password)

        if (result.data && result.data.session?.user) {
          this.user = result.data.session.user as User
          await this.syncUserData()
        }

        return result
      }
      catch (error) {
        this.error = 'Login failed'
        throw error
      }
      finally {
        this.loading = false
      }
    },

    /**
     * Register new user
     */
    async register(email: string, password: string, fullName: string, metadata?: UserMetadata, profileData?: { phone?: string, address?: string, postalCode?: string, city?: string, country?: string }): Promise<AuthResult<RegisterResponse>> {
      try {
        this.loading = true
        this.error = null

        const { signUpWithEmail } = useSupabaseAuth()
        const result = await signUpWithEmail(email, password, fullName, metadata, profileData)

        if (result.data && result.data.session?.user) {
          this.user = result.data.session.user as User
          await this.syncUserData()
        }

        return result
      }
      catch (error) {
        this.error = 'Registration failed'
        throw error
      }
      finally {
        this.loading = false
      }
    },

    /**
     * Logout user
     */
    async logout(): Promise<void> {
      try {
        this.loading = true
        this.error = null

        const { signOut } = useSupabaseAuth()

        await signOut()

        // Clear state
        this.user = null
        this.userData = null
        this.error = null

        const sharedUserData = useState<UserData | null>('auth-user-data', () => null)

        sharedUserData.value = null
      }
      catch (error) {
        console.error('[AuthStore] Logout error:', error)
        this.error = 'Logout failed'
        throw error
      }
      finally {
        this.loading = false
      }
    },

    /**
     * Update user session (called by auth state change listener)
     */
    setUser(user: User | null) {
      this.user = user

      if (user) {
        // Sync data when user is set
        this.syncUserData()
      }
      else {
        // Clear data when user is removed
        this.userData = null

        const sharedUserData = useState<UserData | null>('auth-user-data', () => null)

        sharedUserData.value = null
      }
    },

    /**
     * Clear error state
     */
    clearError() {
      this.error = null
    },

    // ==========================================================================
    // PERMISSION CHECKS (NEW SYSTEM)
    // ==========================================================================

    /**
     * Check if user has a specific permission
     *
     * @param resource - Resource name (e.g., 'camps', 'users', 'finance')
     * @param action - Action name (e.g., 'create', 'read', 'update', 'delete', 'manage')
     * @returns boolean - true if user has permission
     *
     * @example
     * authStore.can('camps', 'create') // Check if user can create camps
     */
    can(resource: string, action: string): boolean {
      if (!this.permissions)
        return false

      const resourcePerms = this.permissions[resource]
      if (!resourcePerms)
        return false

      // Check exact permission or if they have 'manage' (which includes all actions)
      return resourcePerms[action] || (action !== 'manage' && resourcePerms.manage) || false
    },

    /**
     * Check if user has any of the specified permissions
     *
     * @example
     * authStore.canAny([
     *   { resource: 'camps', action: 'read' },
     *   { resource: 'events', action: 'read' }
     * ])
     */
    canAny(permissions: Array<{ resource: string, action: string }>): boolean {
      return permissions.some(p => this.can(p.resource, p.action))
    },

    /**
     * Check if user has all of the specified permissions
     *
     * @example
     * authStore.canAll([
     *   { resource: 'camps', action: 'create' },
     *   { resource: 'camps', action: 'update' }
     * ])
     */
    canAll(permissions: Array<{ resource: string, action: string }>): boolean {
      return permissions.every(p => this.can(p.resource, p.action))
    },

    /**
     * Check if user can manage a resource (has full CRUD access)
     */
    canManage(resource: string): boolean {
      return this.can(resource, 'manage')
    },

    /**
     * Get all permissions for a specific resource
     *
     * @example
     * authStore.getResourcePermissions('camps')
     * // Returns: { create: true, read: true, update: true, delete: false }
     */
    getResourcePermissions(resource: string): Record<string, boolean> {
      return this.permissions?.[resource] ?? {}
    },

  },
})
